import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Users,
  ArrowDownRight, 
  ArrowUpRight,
  LogOut,
  Menu,
  X,
  Shield,
  Code,
  Clock,
  Layout,
  AlertTriangle,
  Settings,
  FileText,
  Gift,
  TrendingUp
} from 'lucide-react';
import { Toaster } from './ui/sonner';
import LanguageSelector from './LanguageSelector';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_sgficapital/artifacts/i5d6sap9_LOGO%20SGFI-nuevo.png';
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const SESSION_TIMEOUT = 3 * 60 * 1000; // 3 minutes in milliseconds
const WARNING_TIME = 30 * 1000; // Show warning 30 seconds before timeout

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingDeposits, setPendingDeposits] = useState(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);
  
  // Session timeout state
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(WARNING_TIME / 1000);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);

  // Fetch all pending counts
  const fetchPendingCounts = useCallback(async () => {
    try {
      // Fetch pending users
      const usersResponse = await axios.get(`${API_URL}/admin/users/pending`);
      setPendingCount(usersResponse.data.length || 0);
      
      // Fetch admin stats for deposits and withdrawals
      const statsResponse = await axios.get(`${API_URL}/admin/stats`);
      setPendingDeposits(statsResponse.data.pending_deposits || 0);
      setPendingWithdrawals(statsResponse.data.pending_withdrawals || 0);
    } catch (error) {
      // Error silenced: 'Error fetching pending counts:', error);
    }
  }, []);

  // Reset session timeout
  const resetTimeout = useCallback(() => {
    // Clear existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    setShowTimeoutWarning(false);
    setTimeRemaining(WARNING_TIME / 1000);

    // Set warning timeout (shows warning 30s before session expires)
    warningTimeoutRef.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      setTimeRemaining(WARNING_TIME / 1000);
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT - WARNING_TIME);

    // Set actual timeout
    timeoutRef.current = setTimeout(() => {
      handleSessionExpired();
    }, SESSION_TIMEOUT);
  }, []);

  // Handle session expired
  const handleSessionExpired = useCallback(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    logout();
    navigate('/login');
  }, [logout, navigate]);

  // Extend session
  const extendSession = () => {
    resetTimeout();
  };

  // Handle logout
  const handleLogout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    logout();
    navigate('/');
  };

  // Initialize timeout and fetch pending counts
  useEffect(() => {
    fetchPendingCounts();
    resetTimeout();

    // Refresh pending counts every 30 seconds
    const pendingInterval = setInterval(fetchPendingCounts, 30000);

    // Listen for custom event when counts change (from PendingApprovals, Deposits, Withdrawals)
    const handleCountsChanged = () => {
      fetchPendingCounts();
    };
    window.addEventListener('pendingCountsChanged', handleCountsChanged);

    // Activity listeners
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    
    const handleActivity = () => {
      if (!showTimeoutWarning) {
        resetTimeout();
      }
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    return () => {
      clearInterval(pendingInterval);
      window.removeEventListener('pendingCountsChanged', handleCountsChanged);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [fetchPendingCounts, resetTimeout, showTimeoutWarning]);

  // Helper function to get badge count for each nav item
  const getBadgeCount = (to) => {
    if (to === '/admin/pending-approvals') return pendingCount;
    if (to === '/admin/deposits') return pendingDeposits;
    if (to === '/admin/withdrawals') return pendingWithdrawals;
    return 0;
  };

  const navItems = [
    { to: '/admin', icon: LayoutDashboard, label: t('nav.dashboard'), end: true },
    { to: '/admin/pending-approvals', icon: Clock, label: t('nav.pendingApprovals'), showBadge: true },
    { to: '/admin/users', icon: Users, label: t('nav.users') },
    { to: '/admin/deposits', icon: ArrowDownRight, label: t('nav.deposits'), showBadge: true },
    { to: '/admin/withdrawals', icon: ArrowUpRight, label: t('nav.withdrawals'), showBadge: true },
    { to: '/admin/campaigns', icon: Gift, label: t('adminCampaigns.title') },
    { to: '/admin/fund-growth', icon: TrendingUp, label: t('fundGrowth.title') },
    { to: '/admin/statement', icon: FileText, label: t('statement.title') },
    // Import/Export hidden as requested
    // { to: '/admin/import-export', icon: Upload, label: t('nav.importExport') },
    { to: '/admin/page-builder', icon: Layout, label: t('nav.pageBuilder') },
    { to: '/admin/css-editor', icon: Code, label: t('nav.cssEditor') },
    { to: '/admin/site-settings', icon: Settings, label: t('common.settings') },
  ];

  // Bottom nav for admin on mobile
  const bottomNavItems = [
    { to: '/admin', icon: LayoutDashboard, label: t('nav.dashboard'), end: true },
    { to: '/admin/pending-approvals', icon: Clock, label: t('nav.pendingApprovals'), showBadge: true },
    { to: '/admin/users', icon: Users, label: t('nav.users') },
    { to: '/admin/deposits', icon: ArrowDownRight, label: t('nav.deposits'), showBadge: true },
  ];

  return (
    <div className="min-h-screen bg-[#0d1218] flex flex-col md:flex-row">
      <Toaster richColors position="top-right" />
      
      {/* Session Timeout Warning Modal */}
      <Dialog open={showTimeoutWarning} onOpenChange={() => {}}>
        <DialogContent className="bg-[#1c262d] border-white/10 max-w-md" data-testid="session-timeout-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              {t('auth.sessionWarning')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-[#93a5af] mb-4">
              {t('auth.sessionExpired')}
            </p>
            
            {/* Countdown Timer */}
            <div className="bg-[#0d1218] rounded-xl p-4 mb-4">
              <div className="text-center">
                <span className="text-4xl font-bold text-amber-400">{timeRemaining}</span>
                <span className="text-[#93a5af] ml-2">{t('time.seconds', 'seconds')}</span>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-amber-500 transition-all duration-1000 ease-linear"
                  style={{ width: `${(timeRemaining / (WARNING_TIME / 1000)) * 100}%` }}
                />
              </div>
            </div>
            
            <p className="text-[#93a5af]/70 text-sm text-center mb-4">
              {t('auth.stayLoggedIn')}
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex-1 border-white/10 text-[#93a5af] hover:bg-white/5"
              data-testid="logout-session-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('common.logout')}
            </Button>
            <Button
              onClick={extendSession}
              className="flex-1 bg-[#2e8036] hover:bg-[#247029]"
              data-testid="extend-session-btn"
            >
              {t('common.continue')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex flex-col w-64 bg-[#1c262d] border-r border-white/10 fixed h-full">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <NavLink to="/" className="block hover:opacity-80 transition-opacity">
            <img src={LOGO_URL} alt="SGFI Capital" className="h-8 w-auto" />
          </NavLink>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? 'bg-[#2e8036]/20 text-[#2e8036]'
                    : 'text-[#93a5af] hover:bg-white/5 hover:text-white'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
              {/* Badge for pending items */}
              {item.showBadge && getBadgeCount(item.to) > 0 && (
                <span 
                  className="ml-auto bg-[#2e8036] text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center"
                  data-testid={`badge-${item.to.split('/').pop()}`}
                >
                  {getBadgeCount(item.to)}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Admin Info & Language */}
        <div className="p-4 border-t border-white/10 space-y-3">
          <LanguageSelector />
          
          <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-xl">
            <div className="w-10 h-10 bg-[#2e8036]/20 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#2e8036]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name || 'Admin'}</p>
              <p className="text-[#93a5af] text-xs truncate">{user?.email}</p>
            </div>
          </div>
          
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>{t('common.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden bg-[#1c262d] border-b border-white/10 sticky top-0 z-40">
        <div className="flex items-center justify-between p-4">
          <NavLink to="/admin">
            <img src={LOGO_URL} alt="SGFI Capital" className="h-8 w-auto" />
          </NavLink>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-[#93a5af] hover:text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#1c262d] border-b border-white/10 p-4 space-y-1 shadow-xl">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? 'bg-[#2e8036]/20 text-[#2e8036]'
                      : 'text-[#93a5af] hover:bg-white/5 hover:text-white'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
                {item.showBadge && getBadgeCount(item.to) > 0 && (
                  <span className="ml-auto bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {getBadgeCount(item.to)}
                  </span>
                )}
              </NavLink>
            ))}
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>{t('common.logout')}</span>
            </button>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 md:ml-64 pb-20 md:pb-0">
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#252F38] border-t border-white/10 z-40">
        <div className="flex items-center justify-around py-2">
          {bottomNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `relative flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all ${
                  isActive ? 'text-blue-400' : 'text-slate-400'
                }`
              }
            >
              <div className="relative">
                <item.icon className="w-5 h-5" />
                {item.showBadge && getBadgeCount(item.to) > 0 && (
                  <span className="absolute -top-1 -right-2 bg-emerald-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {getBadgeCount(item.to) > 9 ? '9+' : getBadgeCount(item.to)}
                  </span>
                )}
              </div>
              <span className="text-xs">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
